import usePrivateAxios from '@/common/api/usePrivateAxios';
import { transformNewGeos } from '@/common/utils/geoTransformers';
import { getRailsUpdate } from '@/common/utils/getRailsUpdate';
import { mapToDeletion } from '@/common/utils/railsHelpers';
import { unFormatCurrency } from '@/common/utils/unFormatters';
import { PublicCompanyLeadsFunnelUpdateContractValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema';
import { useMutation } from '@tanstack/react-query';
import { PublicCompanyLeadData } from '../types/publicCompanyLead';
import { AxiosResponse } from 'axios';
import { ErrorResponse } from '@/common/types/errorResponse';

interface UseUpdatePublicCompanyLeadContract {
  publicCompanyLead?: PublicCompanyLeadData;
  onSuccess: (response: AxiosResponse) => void;
  onError: (error: ErrorResponse) => void;
}

export const useUpdatePublicCompanyLeadContract = ({
  publicCompanyLead,
  onSuccess,
  onError,
}: UseUpdatePublicCompanyLeadContract) => {
  const publicAxios = usePrivateAxios();
  return useMutation({
    mutationKey: ['updateContract'],
    mutationFn: async (data: PublicCompanyLeadsFunnelUpdateContractValidationSchema) => {
      const lead = {
        contract_attributes: {
          ...data.contract,
          payment_modality: data.contract.payment_modality?.value || undefined,
          duration_in_months: data.contract.duration_in_months?.value || undefined,
          bill_grouping_mode: data.contract.bill_grouping_mode?.value || undefined,
          bill_by: data.contract.bill_by?.value || undefined,
          cycle: data.contract.cycle?.value || undefined,
          cycle_start_day: data.contract.cycle_start_day?.value ?? undefined,
          closing_to_due_in_days: data.contract.closing_to_due_in_days?.value || undefined,
          due_to_suspension_in_days: data.contract.due_to_suspension_in_days?.value || undefined,
          payment_method: data.contract.payment_method?.value || undefined,
          global_value: data.contract.global_value ? unFormatCurrency(data.contract.global_value) : undefined,
          fiscal_document_requirement: data.contract.fiscal_document_requirement?.value || undefined,
          fiscal_document_consumer: data.contract.fiscal_document_consumer?.value || undefined,
          cnpj_for_fiscal_document: data.contract.cnpj_for_fiscal_document?.value || undefined,
          geo_geoables_attributes: getRailsUpdate(
            publicCompanyLead?.contract?.geo_geoables || [],
            data?.contract?.geo_geoables || [],
            (geo) => geo.geo.id,
            (geo) => geo.value || '',
            mapToDeletion,
            transformNewGeos,
          ),
          default_fuel_price_rule: data?.contract?.default_fuel_price_rule?.value || undefined,
        },
      };

      delete lead.contract_attributes.geo_geoables;
      delete lead.contract_attributes.contract_fees;

      return await publicAxios.put(`/funnel/public/leads/${publicCompanyLead?.id}`, {
        lead,
      });
    },
    onSuccess,
    onError,
  });
};
